import { setLanguage, t } from "./i18n";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
} from "react";
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Ellipsis, PanelLeft, Sparkles } from "lucide-react";
import type { Conversation, ConversationGroup, User } from "@lumen/shared";
import { api } from "./api";
import { applyConversationSeen } from "./conversationSeen";
import {
  conversationTitleChangedEvent,
  knownConversationTitle,
  rememberConversationTitles,
} from "./conversationTitles";
import { ConversationActionMenu } from "./components/chat/ConversationActionMenu";
import { ConfirmDangerDialog } from "./components/shared/ConfirmDangerDialog";
import { ChatPage } from "./pages/chat/ChatPage";
import { SharedChatPage } from "./pages/chat/SharedChatPage";
import { SettingsArea } from "./pages/settings/SettingsArea";
import { InvitationAcceptancePage } from "./pages/InvitationAcceptancePage";
import { useChatKeyboardResize } from "./useChatKeyboardResize";
import { ModelCatalogProvider } from "./ModelCatalogProvider";
import { Login } from "./Login";
import { ShortcutHelpDialog } from "./ShortcutHelpDialog";
import { ConversationSidebar } from "./components/chat/ConversationSidebar";
import type { ProjectWithConversations } from "./components/projects/projectHelpers";
import { ProjectSidebar } from "./components/projects/ProjectSidebar";
import { chatConversationPath } from "./components/projects/projectViewPreference";
import {
  notifyDataChanged,
  PROJECTS_CHANGED_EVENT,
  subscribeToDataChanges,
  subscribeToServerDataChanges,
} from "./components/projects/projectEvents";
import { ProjectEditorPage } from "./pages/projects/ProjectEditorPage";
import { ConversationStatusIndicator } from "./components/shared/ConversationStatusIndicator";
import {
  consumePendingReturnTo,
  isSharedChatPath,
  sessionExpiredEvent,
} from "./route-auth";

type AttentionKind = "completed" | "approval";

function decodeBase64Url(value: string) {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(
    normalized.length + ((4 - (normalized.length % 4)) % 4),
    "=",
  );
  return Uint8Array.from(atob(padded), (character) => character.charCodeAt(0));
}

/** Drop-target id used for the "Ungrouped" section (group ids are server UUIDs). */
const UNGROUPED_DROP_TARGET = "__ungrouped__";

/** localStorage key remembering which group sections the user collapsed. */
const COLLAPSED_GROUPS_STORAGE_KEY = "lumen:collapsed-groups";
const LAST_VIEW_STORAGE_KEY = "lumen:last-view";

function readCollapsedGroupIds(): Set<string> {
  try {
    const raw = window.localStorage.getItem(COLLAPSED_GROUPS_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(
      parsed.filter((value): value is string => typeof value === "string"),
    );
  } catch {
    return new Set();
  }
}

function readLastView(): "chat" | "project" | null {
  try {
    const value = window.localStorage.getItem(LAST_VIEW_STORAGE_KEY);
    return value === "chat" || value === "project" ? value : null;
  } catch {
    return null;
  }
}

function isProjectViewPath(pathname: string) {
  return (
    pathname.startsWith("/projects") ||
    (pathname.startsWith("/settings") && readLastView() === "project")
  );
}

function isTextEntryTarget(target: EventTarget | null) {
  const element = target instanceof HTMLElement ? target : null;
  return Boolean(
    element?.isContentEditable ||
    element?.tagName === "INPUT" ||
    element?.tagName === "TEXTAREA" ||
    element?.tagName === "SELECT",
  );
}

export default function App() {
  const [user, setUser] = useState<User | null | undefined>();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [, setConversationTitleRevision] = useState(0);
  const [groups, setGroups] = useState<ConversationGroup[]>([]);
  const [projects, setProjects] = useState<Array<{ id: string; name: string }>>(
    [],
  );
  const [conversationSearch, setConversationSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [shortcutHelpOpen, setShortcutHelpOpen] = useState(false);
  const [menuConversationId, setMenuConversationId] = useState<string | null>(
    null,
  );
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [renamingGroupId, setRenamingGroupId] = useState<string | null>(null);
  const [groupRenameValue, setGroupRenameValue] = useState("");
  const [groupToDelete, setGroupToDelete] = useState<ConversationGroup | null>(
    null,
  );
  const [deletingGroup, setDeletingGroup] = useState(false);
  const [groupError, setGroupError] = useState("");
  const [conversationToDelete, setConversationToDelete] =
    useState<Conversation | null>(null);
  const [deletingConversation, setDeletingConversation] = useState(false);
  const [conversationDeleteError, setConversationDeleteError] = useState("");
  const [dragOverGroupId, setDragOverGroupId] = useState<string | null>(null);
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<Set<string>>(() =>
    readCollapsedGroupIds(),
  );
  const conversationsRefreshInFlight = useRef(false);
  const pendingConversationsRefresh = useRef<(() => Promise<void>) | null>(
    null,
  );
  const conversationsRefreshRevision = useRef(0);
  const menuAnchorRef = useRef<HTMLElement | null>(null);
  const setMenuAnchor = useCallback((node: HTMLButtonElement | null) => {
    menuAnchorRef.current = node;
  }, []);
  const closeMenu = useCallback(() => {
    menuAnchorRef.current?.focus();
    setMenuConversationId(null);
  }, []);
  const lastAttention = useRef<{ kind: AttentionKind; at: number } | null>(
    null,
  );
  const pushSubscriptionActive = useRef(false);
  const audioContext = useRef<AudioContext | null>(null);
  const seenConversationMessages = useRef(new Set<string>());
  const location = useLocation();
  const navigate = useNavigate();
  const previousPathname = useRef(location.pathname);
  const initialLoad = useRef(true);
  useChatKeyboardResize();
  useEffect(() => {
    const refreshTitles = () =>
      setConversationTitleRevision((value) => value + 1);
    window.addEventListener(conversationTitleChangedEvent, refreshTitles);
    return () =>
      window.removeEventListener(conversationTitleChangedEvent, refreshTitles);
  }, []);
  const conversationsByGroup = useMemo(() => {
    const map = new Map<string, Conversation[]>();
    for (const conversation of conversations) {
      if (conversation.pinned || conversation.groupId === null) continue;
      const list = map.get(conversation.groupId) ?? [];
      list.push(conversation);
      map.set(conversation.groupId, list);
    }
    return map;
  }, [conversations]);
  const sharedToken = isSharedChatPath(location.pathname)
    ? location.pathname.match(/^\/share\/([^/]+)\/?$/)?.[1]
    : undefined;
  const refresh = useCallback(async () => {
    if (conversationsRefreshInFlight.current) {
      pendingConversationsRefresh.current = refresh;
      return;
    }
    const revision = ++conversationsRefreshRevision.current;
    conversationsRefreshInFlight.current = true;
    try {
      const query = conversationSearch.trim();
      // Fetches are decoupled so a failure of the groups endpoint (new in this
      // feature) can never take down the pre-existing conversation refresh.
      const [next, nextGroups] = await Promise.allSettled([
        api<Conversation[]>(
          `/conversations${query ? `?q=${encodeURIComponent(query)}` : ""}`,
          { cache: "no-store" },
        ),
        api<ConversationGroup[]>("/conversation-groups", {
          cache: "no-store",
        }),
      ]);
      if (revision === conversationsRefreshRevision.current) {
        if (next.status === "fulfilled") {
          const rows = Array.isArray(next.value) ? next.value : [];
          rememberConversationTitles(rows);
          setConversations(rows);
        }
        if (nextGroups.status === "fulfilled")
          setGroups(Array.isArray(nextGroups.value) ? nextGroups.value : []);
      }
    } finally {
      conversationsRefreshInFlight.current = false;
      const pending = pendingConversationsRefresh.current;
      pendingConversationsRefresh.current = null;
      if (pending) void pending();
    }
  }, [conversationSearch]);
  // Lightweight project list used by the conversation action menu so a chat
  // conversation can be moved into a project without switching views.
  const refreshProjects = useCallback(async () => {
    try {
      const rows = await api<ProjectWithConversations[]>("/projects", {
        cache: "no-store",
        notifyDataChange: false,
      });
      setProjects(
        Array.isArray(rows)
          ? rows.map((project) => ({ id: project.id, name: project.name }))
          : [],
      );
    } catch {
      // The action menu falls back to an empty project list; the Project view
      // sidebar reconciles on its own data flow.
    }
  }, []);
  const playAttentionSound = useCallback((kind: AttentionKind) => {
    if (typeof AudioContext === "undefined") return;
    const context =
      audioContext.current ?? (audioContext.current = new AudioContext());
    void context.resume().then(() => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const start = context.currentTime;
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(
        kind === "approval" ? 520 : 660,
        start,
      );
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.08, start + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.18);
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start(start);
      oscillator.stop(start + 0.18);
    });
  }, []);
  const announceAttention = useCallback(
    (kind: AttentionKind) => {
      const now = Date.now();
      if (
        lastAttention.current?.kind === kind &&
        now - lastAttention.current.at < 1500
      )
        return;
      lastAttention.current = { kind, at: now };
      playAttentionSound(kind);
      if (
        document.hidden &&
        !pushSubscriptionActive.current &&
        typeof Notification !== "undefined" &&
        Notification.permission === "granted"
      ) {
        const title =
          kind === "approval" ? t("Action required") : t("Response complete");
        const options = {
          body:
            kind === "approval"
              ? t("Command approval is waiting.")
              : t("The response is ready."),
          tag: "lumen-attention",
        };

        if ("serviceWorker" in navigator && import.meta.env.PROD) {
          void navigator.serviceWorker.ready
            .then((registration) =>
              registration.showNotification(title, options),
            )
            .catch(() => undefined);
        } else {
          new Notification(title, options);
        }
      }
    },
    [playAttentionSound],
  );
  const requestDesktopNotifications = useCallback(() => {
    const subscribe = async () => {
      if (
        !import.meta.env.PROD ||
        !("serviceWorker" in navigator) ||
        !("PushManager" in window)
      )
        return;
      const { publicKey } = await api<{ publicKey: string | null }>(
        "/push/public-key",
        { notifyDataChange: false },
      );
      if (!publicKey) return;
      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription)
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: decodeBase64Url(publicKey),
        });
      await api("/push/subscription", {
        method: "POST",
        body: JSON.stringify(subscription.toJSON()),
        notifyDataChange: false,
      });
      pushSubscriptionActive.current = true;
    };
    const permission =
      typeof Notification !== "undefined"
        ? Notification.permission === "default"
          ? Notification.requestPermission()
          : Promise.resolve(Notification.permission)
        : Promise.resolve("denied" as NotificationPermission);
    void permission.then((value) => {
      if (value === "granted") void subscribe().catch(() => undefined);
    });
  }, []);
  useEffect(() => {
    if (sharedToken) {
      void api<{ defaultLanguage: "en" | "fr" }>("/public/settings").then(
        ({ defaultLanguage }) => setLanguage(defaultLanguage),
      );
      setUser(null);
      return;
    }
    api<User>("/me")
      .then((value) => {
        void setLanguage(value.language ?? value.defaultLanguage);
        setUser(value);
        const pendingReturnTo = consumePendingReturnTo();
        if (pendingReturnTo !== "/")
          navigate(pendingReturnTo, { replace: true });
        const revision = conversationsRefreshRevision.current;
        void api<Conversation[]>("/conversations").then((rows) => {
          if (revision !== conversationsRefreshRevision.current) return;
          const conversations = Array.isArray(rows) ? rows : [];
          rememberConversationTitles(conversations);
          setConversations(conversations);
        });
      })
      .catch(() => {
        void api<{ defaultLanguage: "en" | "fr" }>("/public/settings").then(
          ({ defaultLanguage }) => setLanguage(defaultLanguage),
        );
        setUser(null);
      });
  }, [navigate, sharedToken]);
  useEffect(() => {
    const expireSession = () => {
      setUser(null);
      setConversations([]);
      setGroups([]);
    };
    window.addEventListener(sessionExpiredEvent, expireSession);
    return () => window.removeEventListener(sessionExpiredEvent, expireSession);
  }, []);
  useEffect(() => {
    if (!user) return;
    void refreshProjects();
    const timeout = window.setTimeout(() => void refresh(), 200);
    return () => window.clearTimeout(timeout);
  }, [conversationSearch, refresh, refreshProjects, user]);
  useEffect(() => {
    if (
      !user ||
      !import.meta.env.PROD ||
      typeof Notification === "undefined" ||
      Notification.permission !== "granted"
    )
      return;
    requestDesktopNotifications();
  }, [requestDesktopNotifications, user]);
  useEffect(() => {
    if (!user) return;
    const refreshIfVisible = () => {
      if (document.visibilityState !== "visible") return;
      window.dispatchEvent(new Event(PROJECTS_CHANGED_EVENT));
      void refresh();
      void refreshProjects();
    };
    const refreshOnFocus = () => refreshIfVisible();
    window.addEventListener("focus", refreshOnFocus);
    window.addEventListener("online", refreshOnFocus);
    document.addEventListener("visibilitychange", refreshOnFocus);
    const unsubscribeFromDataChanges = subscribeToDataChanges(refreshIfVisible);
    const unsubscribeFromServerDataChanges = subscribeToServerDataChanges(
      () => {
        if (document.visibilityState === "visible") notifyDataChanged();
      },
    );
    return () => {
      window.removeEventListener("focus", refreshOnFocus);
      window.removeEventListener("online", refreshOnFocus);
      document.removeEventListener("visibilitychange", refreshOnFocus);
      unsubscribeFromDataChanges();
      unsubscribeFromServerDataChanges();
    };
  }, [conversationSearch, refresh, refreshProjects, user]);
  useEffect(() => {
    if (!import.meta.env.PROD || !("serviceWorker" in navigator)) return;
    let active = true;
    let registration: ServiceWorkerRegistration | undefined;
    let refreshing = false;
    const reloadForUpdate = () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    };
    const checkForUpdate = () => {
      if (active && document.visibilityState === "visible")
        void registration?.update();
    };
    navigator.serviceWorker.addEventListener(
      "controllerchange",
      reloadForUpdate,
    );
    document.addEventListener("visibilitychange", checkForUpdate);
    const interval = window.setInterval(checkForUpdate, 60 * 60 * 1000);
    void navigator.serviceWorker
      .register("/sw.js", { updateViaCache: "none" })
      .then((nextRegistration) => {
        if (!active) return;
        registration = nextRegistration;
        return nextRegistration.update();
      })
      .catch(() => undefined);
    return () => {
      active = false;
      navigator.serviceWorker.removeEventListener(
        "controllerchange",
        reloadForUpdate,
      );
      document.removeEventListener("visibilitychange", checkForUpdate);
      window.clearInterval(interval);
    };
  }, []);
  useEffect(() => {
    const wasProjectsView = isProjectViewPath(previousPathname.current);
    const isProjectsView = isProjectViewPath(location.pathname);
    const isMobile = !window.matchMedia("(min-width: 1024px)").matches;
    const isStoredProjectViewRedirect =
      location.pathname === "/" && readLastView() === "project";
    if (initialLoad.current && isProjectsView && isMobile) setOpen(true);
    else if (wasProjectsView === isProjectsView) setOpen(false);
    previousPathname.current = location.pathname;
    if (!isStoredProjectViewRedirect) initialLoad.current = false;
  }, [location.pathname, location.search]);
  useEffect(() => {
    if (location.pathname !== "/") return;
    if (readLastView() === "project") navigate("/projects", { replace: true });
  }, [location.pathname, navigate]);
  useEffect(() => {
    const nextView = location.pathname.startsWith("/projects")
      ? "project"
      : location.pathname === "/" || location.pathname.startsWith("/c/")
        ? "chat"
        : null;
    if (
      !nextView ||
      (location.pathname === "/" && readLastView() === "project")
    )
      return;
    try {
      window.localStorage.setItem(LAST_VIEW_STORAGE_KEY, nextView);
    } catch {
      // Local storage can be unavailable in private or restricted browsing.
    }
  }, [location.pathname]);
  const markConversationSeen = useCallback(
    async (conversation: Conversation) => {
      const messageId =
        conversation.attention === "response-ready"
          ? conversation.attentionMessageId
          : null;
      if (!messageId) return;
      const key = `${conversation.id}:${messageId}`;
      if (seenConversationMessages.current.has(key)) return;
      seenConversationMessages.current.add(key);
      try {
        const updated = await api<Conversation>(
          `/conversations/${conversation.id}/seen`,
          { method: "POST" },
        );
        if (updated.lastSeenMessageId !== messageId) return;
        setConversations((current) =>
          applyConversationSeen(current, conversation.id, messageId),
        );
      } catch {
        seenConversationMessages.current.delete(key);
      }
    },
    [],
  );
  useEffect(() => {
    const selectedId = location.pathname.match(/^\/c\/([^/]+)$/)?.[1];
    const selected = conversations.find(
      (conversation) => conversation.id === selectedId,
    );
    if (selected) markConversationSeen(selected);
  }, [conversations, location.pathname, markConversationSeen]);
  const toggleSidebar = useCallback(() => {
    if (window.matchMedia("(min-width: 1024px)").matches)
      setSidebarVisible((value) => !value);
    else setOpen((value) => !value);
  }, []);
  const navigateConversationHistory = useCallback(
    (direction: -1 | 1) => {
      if (!conversations.length) return;
      const currentId = location.pathname.match(/^\/c\/([^/]+)$/)?.[1];
      const currentIndex = conversations.findIndex(
        (conversation) => conversation.id === currentId,
      );
      const nextIndex =
        currentIndex < 0
          ? direction > 0
            ? 0
            : conversations.length - 1
          : currentIndex + direction;
      const next = conversations[nextIndex];
      if (next) navigate(`/c/${next.id}`);
    },
    [conversations, location.pathname, navigate],
  );
  useEffect(() => {
    if (!user) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "F1") {
        event.preventDefault();
        setShortcutHelpOpen((value) => !value);
        return;
      }
      if (event.key === "Escape" && shortcutHelpOpen) {
        event.preventDefault();
        setShortcutHelpOpen(false);
        return;
      }
      if (!(event.metaKey || event.ctrlKey) || event.altKey) return;
      if (event.code === "KeyO" && event.shiftKey) {
        event.preventDefault();
        toggleSidebar();
        return;
      }
      if (
        (event.key === "ArrowUp" || event.key === "ArrowDown") &&
        !event.shiftKey &&
        !isTextEntryTarget(event.target)
      ) {
        event.preventDefault();
        navigateConversationHistory(event.key === "ArrowUp" ? -1 : 1);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [navigateConversationHistory, shortcutHelpOpen, toggleSidebar, user]);
  if (sharedToken) return <SharedChatPage token={sharedToken} />;
  if (user === undefined)
    return (
      <div className="grid min-h-screen place-items-center">
        <Sparkles className="animate-pulse text-lumen-600" size={32} />
      </div>
    );
  if (!user) return <Login />;
  const admin =
    user.sitePermissions.includes("site:providers:read") ||
    user.sitePermissions.includes("site:tools:read");
  const pinnedConversations = conversations.filter(
    (conversation) => conversation.pinned,
  );
  const recentConversations = conversations.filter(
    (conversation) => !conversation.pinned,
  );
  const ungroupedConversations = conversations.filter(
    (conversation) => !conversation.pinned && conversation.groupId === null,
  );
  const menuConversation = menuConversationId
    ? conversations.find(
        (conversation) => conversation.id === menuConversationId,
      )
    : undefined;
  const updateConversation = async (
    conversation: Conversation,
    patch: {
      pinned?: boolean;
      groupId?: string | null;
      surface?: "chat" | "project";
    },
  ) => {
    const updated = await api<Conversation>(
      `/conversations/${conversation.id}`,
      {
        method: "PATCH",
        body: JSON.stringify(patch),
      },
    );
    setConversations((current) =>
      current
        .map((item) => (item.id === updated.id ? updated : item))
        .sort(
          (a, b) =>
            Number(b.pinned) - Number(a.pinned) ||
            Date.parse(b.updatedAt) - Date.parse(a.updatedAt),
        ),
    );
  };
  const moveConversation = (
    conversation: Conversation,
    groupId: string | null,
  ) => {
    // Failures are reconciled by the 1s conversation poll; never let a move
    // become an unhandled rejection.
    void updateConversation(conversation, { groupId }).catch(() => undefined);
  };
  const moveConversationToProject = (
    conversation: Conversation,
    projectId: string,
  ) => {
    // Moving to a project switches the conversation surface. Remove it from
    // the chat list once the server confirms the move; the Project view
    // sidebar refreshes on the data-change event this PATCH triggers.
    void updateConversation(conversation, {
      surface: "project",
      groupId: projectId,
    })
      .then(() => {
        setConversations((current) =>
          current.filter((item) => item.id !== conversation.id),
        );
        window.dispatchEvent(new Event(PROJECTS_CHANGED_EVENT));
        void refreshProjects();
        if (location.pathname === `/c/${conversation.id}`)
          navigate(`/projects/c/${conversation.id}`, { replace: true });
      })
      .catch(() => undefined);
  };
  const toggleGroupCollapsed = (groupId: string) => {
    setCollapsedGroupIds((current) => {
      const next = new Set(current);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      try {
        window.localStorage.setItem(
          COLLAPSED_GROUPS_STORAGE_KEY,
          JSON.stringify([...next]),
        );
      } catch {
        // Storage may be unavailable (private mode); the in-memory toggle
        // still applies for this session.
      }
      return next;
    });
  };
  const createGroupWithName = async (
    name: string,
  ): Promise<ConversationGroup | null> => {
    const trimmed = name.trim();
    if (!trimmed) return null;
    try {
      const created = await api<ConversationGroup>("/conversation-groups", {
        method: "POST",
        body: JSON.stringify({ name: trimmed }),
      });
      setGroups((current) => [...current, created]);
      return created;
    } catch {
      return null;
    }
  };
  const createGroup = async () => {
    const name = newGroupName.trim();
    if (!name) return;
    setGroupError("");
    const created = await createGroupWithName(name);
    if (created) {
      setNewGroupName("");
      setCreatingGroup(false);
    } else {
      setGroupError(t("Could not create group."));
    }
  };
  const renameGroup = async (group: ConversationGroup) => {
    const name = groupRenameValue.trim();
    if (!name || name === group.name) {
      setRenamingGroupId(null);
      setGroupRenameValue("");
      return;
    }
    setGroupError("");
    try {
      const updated = await api<ConversationGroup>(
        `/conversation-groups/${group.id}`,
        {
          method: "PATCH",
          body: JSON.stringify({ name }),
        },
      );
      setGroups((current) =>
        current.map((item) => (item.id === updated.id ? updated : item)),
      );
      setRenamingGroupId(null);
      setGroupRenameValue("");
    } catch {
      setGroupError(t("Could not rename group."));
    }
  };
  const deleteGroup = async (group: ConversationGroup) => {
    setDeletingGroup(true);
    setGroupError("");
    try {
      await api<void>(`/conversation-groups/${group.id}`, {
        method: "DELETE",
      });
      setGroups((current) => current.filter((item) => item.id !== group.id));
      setConversations((current) =>
        current.map((item) =>
          item.groupId === group.id ? { ...item, groupId: null } : item,
        ),
      );
      setGroupToDelete(null);
    } catch {
      setGroupError(t("Could not delete group."));
    } finally {
      setDeletingGroup(false);
    }
  };
  const deleteConversation = async (conversation: Conversation) => {
    setDeletingConversation(true);
    setConversationDeleteError("");
    try {
      await api<void>(`/conversations/${conversation.id}`, {
        method: "DELETE",
      });
      setConversations((current) =>
        current.filter((item) => item.id !== conversation.id),
      );
      setConversationToDelete(null);
      if (location.pathname === `/c/${conversation.id}`)
        navigate("/", { replace: true });
      void refresh();
    } catch (reason) {
      setConversationDeleteError(
        reason instanceof Error ? reason.message : t("Could not delete chat"),
      );
    } finally {
      setDeletingConversation(false);
    }
  };
  const handleDragStart = (
    event: DragEvent<HTMLDivElement>,
    conversation: Conversation,
  ) => {
    event.dataTransfer.setData("text/plain", conversation.id);
    event.dataTransfer.effectAllowed = "move";
  };
  const handleDragOver = (
    event: DragEvent<HTMLDivElement>,
    target: string | null,
  ) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setDragOverGroupId(target);
  };
  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null))
      setDragOverGroupId(null);
  };
  const handleDrop = (
    event: DragEvent<HTMLDivElement>,
    target: string | null,
  ) => {
    event.preventDefault();
    setDragOverGroupId(null);
    const conversationId = event.dataTransfer.getData("text/plain");
    if (!conversationId) return;
    const conversation = conversations.find(
      (item) => item.id === conversationId,
    );
    if (conversation)
      moveConversation(
        conversation,
        target === UNGROUPED_DROP_TARGET ? null : target,
      );
  };
  const conversationLink = (conversation: Conversation) => {
    const selected = location.pathname.split("/")[2] === conversation.id;
    const showAttention =
      conversation.attention === "action-required" ||
      (conversation.attention === "response-ready" && !selected);
    return (
      <div
        key={conversation.id}
        data-conversation-attention={
          showAttention ? conversation.attention : undefined
        }
        draggable
        onDragStart={(event) => handleDragStart(event, conversation)}
        onDragEnd={() => setDragOverGroupId(null)}
        className={`group flex items-center rounded-xl transition ${selected ? "bg-white font-medium text-lumen-800 shadow-sm dark:bg-white/10 dark:text-lumen-200" : "text-stone-600 hover:bg-white/70 dark:text-stone-400 dark:hover:bg-white/5"}`}
      >
        <Link
          to={chatConversationPath(conversation.id, location.pathname)}
          onClick={() => {
            void markConversationSeen(conversation);
            setOpen(false);
          }}
          className="min-w-0 flex-1 truncate rounded-l-xl px-3 py-2 text-sm"
        >
          {conversation.title === "New conversation"
            ? (knownConversationTitle(conversation.id) ?? conversation.title)
            : conversation.title}
        </Link>
        <ConversationStatusIndicator
          conversation={conversation}
          selected={selected}
        />
        <button
          type="button"
          id={`conversation-menu-${conversation.id}`}
          ref={
            menuConversationId === conversation.id ? setMenuAnchor : undefined
          }
          onClick={() =>
            setMenuConversationId((current) =>
              current === conversation.id ? null : conversation.id,
            )
          }
          aria-haspopup="menu"
          aria-expanded={menuConversationId === conversation.id}
          className={`mr-1 rounded-lg p-1.5 transition hover:bg-white hover:text-lumen-700 focus-visible:opacity-100 dark:hover:bg-white/10 dark:hover:text-lumen-300 ${selected ? "opacity-100" : "opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 pointer-coarse:opacity-100"} text-stone-400`}
          title={t("Conversation actions")}
          aria-label={t("Conversation actions")}
        >
          <Ellipsis size={14} />
        </button>
      </div>
    );
  };
  const projectsMode = isProjectViewPath(location.pathname);
  const sidebar = projectsMode ? (
    <ProjectSidebar user={user} setOpen={setOpen} />
  ) : (
    <ConversationSidebar
      user={user}
      setOpen={setOpen}
      conversationSearch={conversationSearch}
      setConversationSearch={setConversationSearch}
      groups={groups}
      collapsedGroupIds={collapsedGroupIds}
      dragOverGroupId={dragOverGroupId}
      handleDragOver={handleDragOver}
      handleDragLeave={handleDragLeave}
      handleDrop={handleDrop}
      conversationLink={conversationLink}
      ungroupedConversations={ungroupedConversations}
      pinnedConversations={pinnedConversations}
      recentConversations={recentConversations}
      conversationsByGroup={conversationsByGroup}
      renamingGroupId={renamingGroupId}
      setRenamingGroupId={setRenamingGroupId}
      groupRenameValue={groupRenameValue}
      setGroupRenameValue={setGroupRenameValue}
      setGroupToDelete={setGroupToDelete}
      toggleGroupCollapsed={toggleGroupCollapsed}
      renameGroup={renameGroup}
      conversations={conversations}
      creatingGroup={creatingGroup}
      setCreatingGroup={setCreatingGroup}
      newGroupName={newGroupName}
      setNewGroupName={setNewGroupName}
      createGroup={createGroup}
      groupError={groupError}
    />
  );
  const content = (
    <div className="app-shell flex h-[100dvh] overflow-hidden">
      <div
        className={`h-full min-h-0 shrink-0 ${new URLSearchParams(location.search).get("editorWindow") === "1" ? "hidden" : open ? "mobile-fullscreen fixed inset-0 z-40 lg:static lg:block" : sidebarVisible ? "hidden lg:block" : "hidden"}`}
      >
        {open && (
          <button
            className="absolute inset-0 bg-black/30 lg:hidden"
            onClick={() => setOpen(false)}
            aria-label={t("Close sidebar")}
          />
        )}
        <div className="relative flex size-full min-h-0 sm:w-72 lg:w-auto">
          {sidebar}
        </div>
      </div>
      <main className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header
          className={`${new URLSearchParams(location.search).get("editorWindow") === "1" ? "hidden" : "flex"} h-14 shrink-0 items-center border-b border-stone-200 px-4 lg:hidden dark:border-white/10`}
        >
          <button onClick={toggleSidebar} className="button p-2">
            <PanelLeft size={19} />
          </button>
          <span className="ml-2 font-bold">Lumen</span>
        </header>
        <Routes>
          <Route
            path="/"
            element={
              <ChatPage
                user={user}
                refreshConversations={refresh}
                onAttention={announceAttention}
                onPrepareAttention={requestDesktopNotifications}
                onToggleSidebar={toggleSidebar}
              />
            }
          />
          <Route
            path="/c/:id/*"
            element={
              <ChatPage
                user={user}
                refreshConversations={refresh}
                onAttention={announceAttention}
                onPrepareAttention={requestDesktopNotifications}
                onToggleSidebar={toggleSidebar}
              />
            }
          />
          <Route
            path="/projects"
            element={
              <div className="flex flex-1 items-center justify-center p-8 text-center text-stone-400">
                {t("Select a project or create a new one.")}
              </div>
            }
          />
          <Route
            path="/projects/new"
            element={<ProjectEditorPage user={user} />}
          />
          <Route
            path="/projects/edit/:projectId"
            element={<ProjectEditorPage user={user} />}
          />
          <Route
            path="/projects/conversations/new"
            element={
              <ChatPage
                key="project"
                user={user}
                refreshConversations={refresh}
                onAttention={announceAttention}
                onPrepareAttention={requestDesktopNotifications}
                onToggleSidebar={toggleSidebar}
              />
            }
          />
          <Route
            path="/projects/c/:id/*"
            element={
              <ChatPage
                key="project"
                user={user}
                refreshConversations={refresh}
                onAttention={announceAttention}
                onPrepareAttention={requestDesktopNotifications}
                onToggleSidebar={toggleSidebar}
              />
            }
          />
          <Route
            path="/settings/*"
            element={
              <SettingsArea
                user={user}
                onUserChange={setUser}
                onConversationsImported={refresh}
              />
            }
          />
          <Route
            path="/accept-invitation"
            element={<InvitationAcceptancePage />}
          />
          <Route
            path="/admin/*"
            element={
              <Navigate
                to={admin ? "/settings/providers" : "/settings/profile"}
                replace
              />
            }
          />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
      {shortcutHelpOpen && (
        <ShortcutHelpDialog onClose={() => setShortcutHelpOpen(false)} />
      )}
      {menuConversation && (
        <ConversationActionMenu
          open
          onClose={closeMenu}
          anchorRef={menuAnchorRef}
          conversation={menuConversation}
          groups={groups}
          projects={projects}
          onTogglePin={() => {
            void updateConversation(menuConversation, {
              pinned: !menuConversation.pinned,
            }).catch(() => undefined);
          }}
          onMoveToGroup={(groupId) =>
            moveConversation(menuConversation, groupId)
          }
          onCreateGroup={createGroupWithName}
          onMoveToProject={(projectId) =>
            moveConversationToProject(menuConversation, projectId)
          }
          onDelete={() => {
            setConversationDeleteError("");
            setConversationToDelete(menuConversation);
          }}
        />
      )}
      {conversationToDelete && (
        <ConfirmDangerDialog
          title={t("Delete this chat?")}
          description={t(
            "This permanently deletes the conversation, all message versions, and any active share link. {{error}}",
            {
              error: conversationDeleteError
                ? ` ${conversationDeleteError}`
                : "",
            },
          )}
          confirmLabel={t("Delete chat")}
          busy={deletingConversation}
          onCancel={() => setConversationToDelete(null)}
          onConfirm={() => void deleteConversation(conversationToDelete)}
        />
      )}
      {groupToDelete && (
        <ConfirmDangerDialog
          title={t("Delete group?")}
          description={t(
            "Conversations in this group will be ungrouped but not deleted. {{error}}",
            { error: groupError ? ` ${groupError}` : "" },
          )}
          confirmLabel={t("Delete group")}
          busy={deletingGroup}
          onCancel={() => setGroupToDelete(null)}
          onConfirm={() => void deleteGroup(groupToDelete)}
        />
      )}
    </div>
  );
  return <ModelCatalogProvider key={user.id}>{content}</ModelCatalogProvider>;
}
